import EmployeeInterface from '../interfaces/EmployeeInterface';
import { FilteredOptionsInterface, makeQueryParamsForRequest } from '../entities/FilteredOptions';
import http from '../helpers/http';
import NewHireInterface from '../interfaces/NewHireInterface';

const Employee = (token: string) => {
  const headers = {
    'x-access-token': token,
  };

  return {
    listPayRatesBySchool: async (schoolId: string, filteredOptions?: FilteredOptionsInterface) => {
      const params = makeQueryParamsForRequest(filteredOptions);
      const result = await http.get(`/employees/pay-rates/${schoolId}`, {
        headers,
        params,
      });
      return result.data;
    },
    actionPayRates: async (userId: string, id: string, action: string, body: any = {}) => {
      const result = await http.put(`/employees/${userId}/pay-rate/${id}/${action}`, body, { headers });
      return result.data;
    },
    listPayRates: async (employeeId: string, filteredOptions?: FilteredOptionsInterface) => {
      const params = makeQueryParamsForRequest(filteredOptions);
      const result = await http.get(`/employees/${employeeId}/pay-rate`, {
        headers,
        params,
      });
      return result.data;
    },
    updatePayRate: async (userId: string, body: any) => {
      const result = await http.put(`/employees/${userId}/pay-rate`, body, { headers });
      return result.data;
    },
    updatePayRateDescription: async (userId: string, id: string, body: any) => {
      const result = await http.put(`/employees/${userId}/pay-rate/${id}`, body, { headers });
      return result.data;
    },
    updatePtoSpec: async (userId: string, body: any) => {
      const result = await http.put(`/employees/${userId}/pto-spec`, body, { headers });
      return result.data;
    },
    update: async (userId: string, body: Partial<EmployeeInterface>) => {
      const result = await http.put(`/employees/${userId}`, body, { headers });
      return result.data;
    },
    list: async (filteredOptions?: FilteredOptionsInterface, signal?: AbortSignal) => {
      const params = makeQueryParamsForRequest(filteredOptions);
      const result = await http.get('/employees', {
        headers,
        params,
        signal,
      });
      return result.data;
    },
    showSsn: async (id: string) => {
      const result = await http.get(`/employees/${id}/ssn`, {
        headers,
      });
      return result.data;
    },
    show: async (id: string) => {
      const result = await http.get(`/employees/${id}`, {
        headers,
      });
      return result.data;
    },
    create: async (body: Partial<EmployeeInterface>) => {
      const result = await http.post(`/employees`, body, { headers });
      return result.data;
    },
    activated: async (id: string) => {
      const result = await http.patch(`/employees/activate/${id}`, {}, { headers });
      return result;
    },
    createNewHire: async (body: Partial<NewHireInterface>) => {
      const result = await http.post(`/employees/new-hire`, body, { headers });
      return result.data;
    },
    revokeNewHire: async (employeeId: string) => {
      const result = await http.delete(`/employees/new-hire/revoke/${employeeId}`, { headers });
      return result.data;
    },
    newHireValidationToken: async (token: string) => {
      const result = await http.get(`/employees/new-hire/validation/${token}`, {});
      return result;
    },
    documentsValidationToken: async (token: string) => {
      const result = await http.get(`/employees/documents/validation/${token}`, {});
      return result;
    },
    acceptNewHire: async (token: string, signature: string) => {
      const result = await http.patch(`/employees/new-hire/accept/${token}`, { signature });
      return result;
    },
    resendNewHire: async (employeeId: string) => {
      const result = await http.patch(`/employees/new-hire/resend/${employeeId}`);
      return result;
    },
    resendPaperwork: async (employeeId: string, body: any) => {
      const result = await http.patch(`/employees/paperwork/resend/${employeeId}`, body, { headers });
      return result;
    },
    resendCredentials: async (employeeId: string) => {
      const result = await http.patch(`/employees/credentials/resend/${employeeId}`, {}, { headers });
      return result;
    },
    removeAccess: async (employeeId: string) => {
      const result = await http.patch(`/employees/new-hire/remove-access/${employeeId}`, {}, { headers });
      return result;
    },
    putOnLeave: async (employeeId: string) => {
      const result = await http.patch(`/employees/new-hire/put-on-leave/${employeeId}`, {}, { headers });
      return result;
    },
    putOnLeaveWithDetails: async (body: any) => {
      const formData = new FormData();

      formData.append('employeeId', body.employeeId);
      formData.append('schoolId', body.schoolId);
      formData.append('firstDayOfLoa', body.firstDayOfLoa);
      formData.append('lastDayOfLoa', body.lastDayOfLoa);
      formData.append('typeOfLoa', body.typeOfLoa);
      formData.append('loaPolicyId', body.loaPolicyId || '');
      formData.append('selectedReason', body.selectedReason || '');
      formData.append('reason', body.reason);
      formData.append('additionalNotes', body.additionalNotes);

      if (body.documents && body.documents.length > 0) {
        body.documents.forEach((doc: File, index: number) => {
          formData.append(`documents[${index}]`, doc);
        });
      }

      const result = await http.post(`/employee-loa`, formData, {
        headers: {
          ...headers,
          'Content-Type': 'multipart/form-data',
        },
      });
      return result;
    },
    getPutOnLeaveDetails: async (employeeId: string) => {
      const result = await http.get(`/employee-loa/${employeeId}`, { headers });
      return result;
    },
    endPutOnLeave: async (employeeId: string, actualLastDay?: string) => {
      const body = actualLastDay ? { actualLastDay } : {};
      const result = await http.patch(`/employees/new-hire/end-put-on-leave/${employeeId}`, body, { headers });
      return result;
    },
    rejectNewHire: async (body: { token: string; rejectReason: string }) => {
      const result = await http.patch(`/employees/new-hire/reject/${body?.token}`, {
        offerLetterRejectReason: body?.rejectReason,
      });
      return result;
    },
    listBySchool: async (schoolId: string) => {
      const result = await http.get(`/employees/${schoolId}/expiration-dates`, {
        headers,
      });
      return result.data;
    },
    listCanReciveSupportTickets: async (schoolId: string) => {
      const result = await http.get(`/employees/can-recive-support-tickets/${schoolId}`, {
        headers,
      });
      return result.data;
    },
    listEmployeesBySchool: async (schoolId: string, search = '', filteredOptions?: FilteredOptionsInterface) => {
      const params = makeQueryParamsForRequest(filteredOptions);
      const result = await http.get(`/employees/by-school/${schoolId}?search=${search}`, {
        headers,
        params,
      });
      return result.data;
    },
    listOverviewBySchool: async (schoolId: string) => {
      const result = await http.get(`/employees/by-school/${schoolId}/overview`, {
        headers,
      });
      return result.data;
    },
    updateMultiplePtoPolices: async (userIds: string[], ptoPolicyId: string) => {
      const result = await http.put(`/employees/update-multiple-pto-policies`, { userIds, ptoPolicyId }, { headers });
      return result.data;
    },
    listSelectStaff: async (schoolId: string) => {
      const result = await http.get(`/employees/selectBySchoolId/${schoolId}`, {
        headers,
      });
      return result.data;
    },
    adminApproveOrDeny: async (action: string, employeeId: string, body = {}) => {
      const result = await http.patch(`/employees/new-hire/admin/${action}/${employeeId}`, body, { headers });
      return result.data;
    },
    listTimeOffs: async (employeeId: string, filteredOptions?: FilteredOptionsInterface) => {
      try {
        const params = makeQueryParamsForRequest(filteredOptions);
        const result = await http.get(`/employees/${employeeId}/time-offs`, {
          headers,
          params,
        });
        return result.data;
      } catch (e) {
        console.error(e);
        return [];
      }
    },
    totalByStatus: async (schoolId: string) => {
      const result = await http.get(`/employees/total-by-status/${schoolId}`, {
        headers,
      });
      return result.data;
    },
    bulkUpdateSupervisors: async (body: { employeeIds: string[]; supervisorId: string }) => {
      const result = await http.put(`/employees/bulk-update-supervisors`, body, { headers });
      return result.data;
    },
  };
};

export default Employee;
